import { Waypoints, Users, ArrowLeftRight, Server } from 'lucide-react';
import { Badge, Card, CardBody, EmptyState } from '../../ui';
import type { WorkstreamOverlap } from '../../features/intelligence/types';

export interface OverlapPanelProps {
  overlaps: WorkstreamOverlap[];
  workstreamCount: number;
  onOpenWorkstream: (id: string) => void;
}

/**
 * Possible overlapping work (section 21e) — surfaces pairs of workstreams that
 * touch the same systems or share keywords, framed as a COLLABORATION prompt
 * ("you two might want to talk"), never as an enforcement or duplicate-work
 * accusation. Shows the shared systems and who to connect — no per-person
 * metrics.
 */
export function OverlapPanel({
  overlaps,
  workstreamCount,
  onOpenWorkstream,
}: OverlapPanelProps) {
  if (overlaps.length === 0) {
    return (
      <EmptyState
        icon={<ArrowLeftRight className="h-6 w-6" />}
        title="No overlapping work detected"
        description={
          workstreamCount < 2
            ? 'Overlap detection compares workstreams by shared systems and keywords — add a couple of workstreams to see suggestions.'
            : 'No workstreams currently share enough systems or keywords to suggest a conversation.'
        }
      />
    );
  }

  return (
    <div className="space-y-3">
      {overlaps.map((o) => (
        <Card key={`${o.a.id}:${o.b.id}`}>
          <CardBody className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Badge tone="warning">
                <ArrowLeftRight className="h-3 w-3" /> possible overlap ·{' '}
                {Math.round(o.score * 100)}%
              </Badge>
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              {[o.a, o.b].map((ws) => (
                <button
                  key={ws.id}
                  type="button"
                  onClick={() => onOpenWorkstream(ws.id)}
                  className="group flex items-start gap-2 rounded-xl border border-surface-border bg-surface-1 px-3 py-2 text-left transition-colors hover:border-accent/40 hover:bg-surface-3"
                >
                  <Waypoints className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-zinc-100 group-hover:text-white">
                      {ws.title || 'Untitled workstream'}
                    </span>
                    <span className="block truncate text-xs text-zinc-500">
                      {ws.engineer} · {ws.currentStage}
                    </span>
                  </span>
                </button>
              ))}
            </div>

            {o.sharedSystems.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-zinc-500">Shared systems:</span>
                {o.sharedSystems.map((s) => (
                  <Badge key={s} tone="neutral">
                    <Server className="h-3 w-3" /> {s}
                  </Badge>
                ))}
              </div>
            )}

            {o.sharedKeywords.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-xs text-zinc-500">Shared keywords:</span>
                {o.sharedKeywords.slice(0, 8).map((k) => (
                  <Badge key={k} tone="neutral">
                    #{k}
                  </Badge>
                ))}
              </div>
            )}

            {o.suggestedContacts.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 border-t border-surface-border pt-3">
                <Users className="h-3.5 w-3.5 text-zinc-500" aria-hidden="true" />
                <span className="text-xs text-zinc-500">Worth connecting:</span>
                {o.suggestedContacts.map((c) => (
                  <Badge key={c} tone="accent">
                    {c}
                  </Badge>
                ))}
              </div>
            )}
          </CardBody>
        </Card>
      ))}
    </div>
  );
}

export default OverlapPanel;
